// DISP-5a — small shared pieces of the dispatch (BL) web path: the status rail, the lines/label-value block and the
// D6 refusal banner (an open slip blocks order edit/cancel: name the BL, offer "Cancel the slip" as a deliberate click, never auto).
import React, { useState } from 'react';
import { targetDate } from './dispatchEta.mjs';
import { isPickup, modeOf, pickupPlaces } from './dispatchFulfilment.mjs';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { formatQty } from '../../utils/format';
import './dispatch.css';
import { isOpenDispatchRefusal, refusalOf } from './dispatchModel.mjs';

// Draft -> Open (in transit) -> Delivered; `cancelled` is shown as its own chip. Same chips as the drawer head (.chip).
export function SlipRail({ status }) {
  const { t } = useTranslation('dispatch');
  if (status === 'cancelled') return <div className="badges"><span className="chip crit">{t('status.cancelled')}</span></div>;
  const steps = ['draft', 'open', 'delivered'];
  return (
    <div className="badges" aria-label={t('rail.label')}>
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          {i > 0 && <span aria-hidden="true" style={{ color: 'var(--text-3, #8a948e)' }}>→</span>}
          <span className={'chip ' + (s === status ? 'info' : 'mut')} aria-current={s === status ? 'step' : undefined}>{t('status.' + s)}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// label / value block of a slip (drawer anatomy: label-value block).
export function SlipFacts({ slip, fmtDate }) {
  const { t } = useTranslation('dispatch');
  const pk = isPickup(slip);   // a pickup slip: collector block + pickup location, no delivery address; labels switch by mode
  const row = (k, v, label) => v ? <React.Fragment key={k}><dt>{t('field.' + (label || k))}</dt><dd>{v}</dd></React.Fragment> : null;
  return (
    <dl className="dsp-facts">
      {row('blNo', slip.dispatch_no || t('noNumberYet'))}
      {row('fulfilment', t('mode.' + modeOf(slip)))}
      {row('eta', targetDate(slip) ? fmtDate(targetDate(slip)) : null, pk ? 'pickupEta' : 'eta')}
      {row('dispatchDate', fmtDate(slip.dispatch_date))}
      {row('vehicle', slip.vehicle, pk ? 'plate' : 'vehicle')}
      {row('driver', slip.driver, pk ? 'collector' : 'driver')}
      {pk && row('pickupPhone', slip.pickup_phone)}
      {pk && row('pickupId', slip.pickup_id_note)}
      {pk ? row('pickupPlace', pickupPlaces(slip).join(', ')) : row('deliverTo', slip.deliver_to)}
      {row('notes', slip.notes)}
    </dl>
  );
}

// Slip lines table. itemOf(sales_id) -> item name from the order payload.
export function SlipLines({ slip, itemOf, showDelivered }) {
  const { t } = useTranslation('dispatch');
  const total = (slip.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  return (
    <div className="gtable-wrap" style={{ overflowX: 'auto' }}>
      <table className="gtable zebra">
        <thead><tr><th>{t('col.item')}</th><th className="r">{t('col.dispatched')}</th>{showDelivered && <th className="r">{t('col.received')}</th>}</tr></thead>
        <tbody>
          {(slip.lines || []).map(l => (
            <tr key={l.id}><td>{itemOf(l.sales_id)}</td><td className="r num">{formatQty(l.quantity)}</td>{showDelivered && <td className="r num">{formatQty(l.delivered_qty)}</td>}</tr>
          ))}
          <tr className="tot"><td>{t('col.total')}</td><td className="r num">{formatQty(total)}</td>{showDelivered && <td />}</tr>
        </tbody>
      </table>
    </div>
  );
}

// Keeps the OPEN_DISPATCH_EXISTS refusal of an order door. note(resp) from the panel's !ok branch; <DispatchRefusal> renders it.
export function useDispatchRefusal() {
  const { i18n } = useTranslation();
  const [refusal, setRefusal] = useState(null);
  const note = resp => { const r = isOpenDispatchRefusal(resp?.data) ? refusalOf(resp, i18n.language) : null; setRefusal(r); return !!r; };
  return [refusal, note, () => setRefusal(null)];
}

export function DispatchRefusal({ refusal, onChanged, onClear, bare = false }) {
  const { t, i18n } = useTranslation('dispatch');
  const [confirming, setConfirming] = useState(null);   // slip_id awaiting the second, deliberate click
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!refusal) return null;
  const open = (refusal.dispatches || []).filter(d => d.status === 'open');
  if (bare && !open.length) return null;
  async function cancelSlip(d) {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.cancelSalesDispatch(d.slip_id, t('cancelReasonFromRefusal'));
      if (!r.ok) { setErr(refusalOf(r, i18n.language, t('err.generic')).text); return; }
      onClear(); if (onChanged) onChanged();
    } catch (e) { setErr(e.message || t('err.generic')); } finally { setBusy(false); }
  }
  return (
    <div className={bare ? undefined : 'err-banner'} role={bare ? undefined : 'alert'} data-testid="dispatch-refusal" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, marginBottom: 10, marginTop: bare ? 8 : 0 }}>
      {refusal.text && <div>{refusal.text} {refusal.code && <code style={{ fontSize: 10.5, fontWeight: 500 }}>{refusal.code}</code>}</div>}
      {open.map(d => confirming === d.slip_id ? (
        <div key={d.slip_id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500 }}>{t('refusal.confirmCancel', { no: d.dispatch_no })}</span>
          <button type="button" className="btn danger" disabled={busy} onClick={() => cancelSlip(d)}>{t('refusal.yesCancel')}</button>
          <button type="button" className="btn" disabled={busy} onClick={() => setConfirming(null)}>{t('refusal.keep')}</button>
        </div>
      ) : (
        <button key={d.slip_id} type="button" className="btn" onClick={() => setConfirming(d.slip_id)}>{t('refusal.cancelSlip', { no: d.dispatch_no })}</button>
      ))}
      {err && <div style={{ fontWeight: 500 }}>{err}</div>}
    </div>
  );
}

// Pill for a slip status, same pill look as the order-page badges (StatusBadge).
const PILL = { draft: ['#f1f5f9', '#64748b'], open: ['#eff6ff', '#2563eb'], delivered: ['#f0fdf4', '#16a34a'], cancelled: ['#fef2f2', '#b91c1c'] };
export function SlipStatusChip({ status, scheduled = false, voidLabel = false }) {   // scheduled = an open slip whose dispatch ETA (target date) is still ahead; voidLabel = direct BL of a cancelled sale
  const { t } = useTranslation('dispatch');
  const [bg, color] = PILL[status] || PILL.draft;
  const st = { background: bg, color, padding: '2px 9px', borderRadius: 9999, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' };
  if (voidLabel) return <span style={st}>{t('directBl.void')}</span>;
  if (scheduled && status === 'open') return <span style={st}>{t('statusShort.scheduled')}</span>;
  return <span style={st}>{t('statusShort.' + status, { defaultValue: status })}</span>;
}
